import React, { useEffect, useState } from 'react';
import { shape, string, func, bool, number } from 'prop-types';
import { SUCCESS } from '@rootstrap/redux-tools';

import Modal from 'components/common/Modal/Modal';
import Button from 'components/common/Button/Button';
import PurchaseModal from 'components/payments/PurchaseModal/PurchaseModal';
import Crown from 'components/icons/membership/Crown';
import { celebrityShape } from 'constants/propTypesShapes';
import { useModal, useSession } from 'hooks';
import AuthModal from 'components/user/AuthModal/AuthModal';
import useProfile from 'hooks/users/useProfile';
import { zIndexModalSecond } from 'styles/common/_constants.scss';

const ICON_SIZE = 20;

const UnlockEventButton = ({ celebrity, title, size, onSuccess, smallBoldFont, eventId, showAuthModal }) => {
  const { toggle: togglePayments, isShowing: isShowingPayments } = useModal();
  const { toggle: toggleAuth, isShowing: isShowingAuth } = useModal();
  const { authenticated } = useSession();

  const {
    profile,
    requestProfile,
    status,
    reset
  } = useProfile();

  const [showedAuth, setShowedAuth] = useState(false);

  // Custom auth success handler to check event subscription status
  const handleAuthSuccess = () => {
    console.log("Local auth success handler called");
    setShowedAuth(true);
    requestProfile();
  };

  // Show payment modal if authenticated, otherwise show auth modal
  const showPaymentsModal = () => {
    console.log("Show payments modal called, authenticated:", authenticated);
    
    if (!authenticated) {
      // If we received the external showAuthModal, use it (with callback)
      if (showAuthModal) {
        console.log("Using external auth modal");
        showAuthModal(() => {
          // This callback should only run after successful login AND data refresh
          console.log("External auth success callback running");
          // Check if the user is already subscribed to the event
          const event = celebrity.events?.find(e => e.id === Number(eventId));
          console.log("Event subscription status:", event?.subscribedToEvent);
          console.log("Event data:", event);
          
          if (event?.subscribedToEvent) {
            // If already subscribed, skip payment
            console.log("User already subscribed, skipping payment");
            onSuccess?.();
          } else {
            // Otherwise, show payment modal
            console.log("User not subscribed, showing payment modal");
            togglePayments();
          }
        });
      } else {
        // Fallback to local auth modal if no external handler provided
        console.log("Using local auth modal");
        setShowedAuth(true);
        toggleAuth();
      }
    } else {
      console.log("User already authenticated, showing payment modal directly");
      togglePayments();
    }
  };

  // Handle auth success flow when using local auth modal
  useEffect(() => {
    if (status === SUCCESS && showedAuth) {
      console.log("Profile update success after local auth");
      // After successful login, check if the event is already purchased
      const event = celebrity.events?.find(e => e.id === Number(eventId));
      console.log("Event subscription status:", event?.subscribedToEvent);
      console.log("Event data:", event);
      
      if (event?.subscribedToEvent) {
        // If already subscribed, skip payment
        console.log("User already subscribed, skipping payment");
        onSuccess?.();
      } else {
        // Otherwise, show payment modal
        console.log("User not subscribed, showing payment modal");
        togglePayments();
      }
      reset();
      setShowedAuth(false);
    }
  }, [status]);

  return (
    <>
      <Button
        size={size}
        type="primary"
        title={title}
        icon={<Crown width={ICON_SIZE} height={ICON_SIZE} />}
        onClick={showPaymentsModal}
        smallBoldFont={smallBoldFont}
        className="subscribe-button"
      />
      <Modal hide={togglePayments} isShowing={isShowingPayments}>
        <PurchaseModal celebrity={celebrity} onSuccess={onSuccess} type='event' eventId={eventId} />
      </Modal>
      {/* Only show local auth modal if no external handler provided */}
      {!showAuthModal && (
        <Modal hide={toggleAuth} isShowing={isShowingAuth} zIndex={zIndexModalSecond}>
          <AuthModal isModal onSuccess={handleAuthSuccess} />
        </Modal>
      )}
    </>
  );
};

UnlockEventButton.defaultProps = {
  size: 'xxsmall',
};

UnlockEventButton.propTypes = {
  celebrity: shape(celebrityShape).isRequired,
  onSuccess: func,
  title: string.isRequired,
  size: string,
  smallBoldFont: bool,
  eventId: number,
  showAuthModal: func
};

export default UnlockEventButton;
